import React from "react";
import { Fan } from "lucide-react";

// ---- tiny decorative building blocks (all non-interactive) ----
const Packet = ({ w = 14, h = 20, color = "#E9C46A", rot = 0, stripe = "#00000022" }) => (
  <div
    className="relative shrink-0 rounded-[3px] border border-black/30 shadow-[1px_1px_0_rgba(0,0,0,0.25)]"
    style={{ width: w, height: h, background: color, transform: `rotate(${rot}deg)` }}
  >
    <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2" style={{ background: stripe }} />
    <div className="absolute inset-x-1 top-1 h-[2px] rounded bg-white/40" />
  </div>
);

const Sachet = ({ color = "#457B9D" }) => (
  <div
    className="h-4 w-3 rounded-sm border border-black/30"
    style={{ background: color, boxShadow: "inset 0 -2px 2px rgba(0,0,0,0.2)" }}
  />
);

// A hanging string of sachets/packets from the ceiling — classic kirana look.
const HangStrip = ({ colors }) => (
  <div className="flex flex-col items-center">
    <div className="h-4 w-[1px] bg-kirana-brown/70" />
    <div className="flex flex-col gap-[2px]">
      {colors.map((c, i) => (
        <Sachet key={`${c}-${i}`} color={c} />
      ))}
    </div>
  </div>
);

// A carton / cardboard box on the floor.
const Carton = ({ w = 42, h = 34, rot = 0, label }) => (
  <div
    className="relative rounded-[3px] border-2 border-[#7a5a3a] shadow-[2px_2px_0_rgba(0,0,0,0.25)]"
    style={{ width: w, height: h, background: "linear-gradient(180deg,#c79a68,#b07f4e)", transform: `rotate(${rot}deg)` }}
  >
    <div className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-[#7a5a3a]/60" />
    <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-[#7a5a3a]/40" />
    {label && (
      <span className="absolute inset-0 flex items-center justify-center text-center font-signboard text-[7px] font-bold text-[#5c3d22]">
        {label}
      </span>
    )}
  </div>
);

const Jar = ({ fill = "#E76F51" }) => (
  <div className="relative h-9 w-7">
    <div className="absolute left-1/2 top-0 h-1.5 w-5 -translate-x-1/2 rounded-t bg-[#9C6644]" />
    <div className="absolute bottom-0 h-7 w-7 overflow-hidden rounded-b-[6px] rounded-t-sm border-2 border-white/50 bg-white/25 backdrop-blur-[1px]">
      <div className="absolute bottom-0 h-4 w-full" style={{ background: fill }}>
        <div className="absolute left-1 top-0 h-1 w-1 rounded-full bg-white/60" />
        <div className="absolute right-1 top-1 h-1 w-1 rounded-full bg-white/50" />
        <div className="absolute left-2 top-2 h-1 w-1 rounded-full bg-white/40" />
      </div>
      <div className="absolute left-1 top-1 h-4 w-1 rounded-full bg-white/40" />
    </div>
  </div>
);

const PACKS_A = ["#E63946", "#F4A261", "#2A9D8F", "#E9C46A", "#457B9D", "#E76F51", "#8AB17D", "#D9A441", "#B5651D", "#6D9DC5", "#C1440E", "#E8C468"];
const PACKS_B = ["#457B9D", "#E9C46A", "#E76F51", "#8AB17D", "#D62828", "#F4A261", "#2A9D8F", "#9C6644", "#E63946", "#D9A441", "#6D9DC5", "#C1440E"];

// Non-interactive decorative backdrop for the 1990s kirana store interior.
export const StoreDecor = () => (
  <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
    {/* back wall */}
    <div className="absolute inset-x-0 top-0 h-[42%] bg-gradient-to-b from-[#C9A66B] to-[#B98E52]" />
    {/* uneven damp wall patches */}
    <div className="absolute left-[20%] top-[8%] h-10 w-16 rotate-2 rounded bg-black/5" />
    <div className="absolute right-[34%] top-[15%] h-8 w-12 -rotate-3 rounded bg-black/5" />
    {/* floor */}
    <div className="absolute inset-x-0 bottom-0 top-[42%] bg-gradient-to-b from-[#8A6B4A] to-[#6E5238]" />
    <div className="absolute inset-x-0 top-[42%] h-1 bg-kirana-brown/40" />
    {/* subtle floor tile seams */}
    <div className="absolute inset-x-0 top-[62%] h-[1px] bg-black/10" />
    <div className="absolute inset-x-0 top-[80%] h-[1px] bg-black/10" />

    {/* tube light + ceiling fan */}
    <div className="absolute left-1/2 top-[3%] h-1.5 w-40 -translate-x-1/2 rounded-full bg-[#EAF6FF] shadow-[0_0_24px_6px_rgba(200,235,255,0.6)]" />
    <div className="absolute left-[46%] top-[1%]">
      <Fan className="animate-fan h-9 w-9 text-kirana-brown/70" strokeWidth={2} />
    </div>

    {/* hanging sachet/packet strips from the ceiling */}
    <div className="absolute left-[30%] top-[2%]"><HangStrip colors={["#E63946", "#457B9D", "#E9C46A", "#2A9D8F", "#E76F51"]} /></div>
    <div className="absolute left-[55%] top-[2%]"><HangStrip colors={["#F4A261", "#8AB17D", "#D62828", "#6D9DC5", "#E9C46A"]} /></div>
    <div className="absolute left-[73%] top-[2%]"><HangStrip colors={["#2A9D8F", "#E63946", "#D9A441", "#457B9D"]} /></div>

    {/* ===== STOCKED SHELVES across the back wall (behind the bright category cards) ===== */}
    {/* shelf plank 1 */}
    <div className="wood-shelf absolute left-[5%] right-[5%] top-[19%] h-3 rounded-sm border-2 border-kirana-brown/60" />
    <div className="absolute left-[6%] right-[6%] top-[13.5%] flex items-end gap-[3px] px-1">
      {PACKS_A.map((c, i) => (
        <Packet key={`s1-${i}`} color={c} w={12 + (i % 3) * 3} h={16 + (i % 4) * 4} rot={(i % 2 ? 1 : -1) * (i % 3)} />
      ))}
    </div>
    {/* shelf plank 2 */}
    <div className="wood-shelf absolute left-[5%] right-[5%] top-[31%] h-3 rounded-sm border-2 border-kirana-brown/60" />
    <div className="absolute left-[6%] right-[6%] top-[25%] flex items-end gap-[3px] px-1">
      {PACKS_B.map((c, i) => (
        <Packet key={`s2-${i}`} color={c} w={11 + (i % 4) * 3} h={15 + (i % 3) * 5} rot={(i % 2 ? -1 : 1) * (i % 2)} stripe="#ffffff33" />
      ))}
    </div>

    {/* ===== WALL DETAILS ===== */}
    {/* old calendar */}
    <div className="absolute right-[8%] top-[5%] h-12 w-9 rotate-2 rounded-sm border-2 border-kirana-brown bg-kirana-cream">
      <div className="h-3 bg-kirana-red" />
      <div className="p-0.5 text-center font-signboard text-[7px] leading-tight text-kirana-brown">1999</div>
    </div>
    {/* faded advertising poster */}
    <div className="absolute left-[6%] top-[5%] h-10 w-24 -rotate-1 rounded-sm border-2 border-kirana-brown bg-[#E76F51] opacity-80">
      <div className="flex h-full items-center justify-center font-signboard text-[9px] font-bold text-white">THANDA MATLAB…</div>
    </div>
    {/* handwritten price board */}
    <div className="absolute right-[20%] top-[6%] h-16 w-14 rotate-1 rounded-sm border-2 border-kirana-brown bg-[#2F4F3A] p-1 shadow">
      <div className="font-signboard text-[6px] leading-[1.5] text-kirana-cream">
        <div className="mb-0.5 border-b border-kirana-cream/40 pb-0.5 text-center font-bold">REYT LIST</div>
        Parle-G ₹4<br />Gold Flake<br />Vimal ₹1<br />Frooti ₹8<br />Poppins ₹2
      </div>
    </div>

    {/* ===== SHOPKEEPER'S GLASS COUNTER (front-left, around the Kaka zone) ===== */}
    {/* wooden counter base */}
    <div className="absolute bottom-[6%] left-[6%] h-20 w-64 rounded-sm border-[3px] border-kirana-brown wood-shelf" />
    {/* glass display front */}
    <div className="absolute bottom-[6%] left-[6%] h-12 w-64 rounded-sm border-[3px] border-white/50 bg-gradient-to-b from-white/25 to-white/10 backdrop-blur-[1px]">
      {/* loose biscuits / toffees inside */}
      <div className="absolute bottom-1 left-2 flex gap-1">
        {["#D9A441", "#C1440E", "#E9C46A", "#8B5A2B", "#E76F51", "#D9A441", "#C77F3A"].map((c, i) => (
          <span key={`b-${i}`} className="h-3 w-3 rounded-full border border-black/30" style={{ background: c }} />
        ))}
      </div>
      <div className="absolute bottom-5 left-3 flex gap-1">
        {["#E63946", "#F4A261", "#2A9D8F", "#457B9D", "#E9C46A"].map((c, i) => (
          <span key={`t-${i}`} className="h-2 w-4 rounded-[2px] border border-black/30" style={{ background: c }} />
        ))}
      </div>
      <div className="absolute right-2 top-1 text-[6px] font-bold text-kirana-brown/70">GLASS COUNTER</div>
    </div>
    {/* candy jars on top of the counter */}
    <div className="absolute bottom-[16%] left-[8%] flex items-end gap-2">
      <Jar fill="#E76F51" />
      <Jar fill="#E9C46A" />
      <Jar fill="#2A9D8F" />
      <Jar fill="#E63946" />
    </div>
    {/* weighing scale */}
    <div className="absolute bottom-[16%] left-[30%] flex flex-col items-center">
      <div className="flex w-12 justify-between">
        <div className="h-2 w-4 rounded-b-full border border-kirana-brown bg-[#cfc3a5]" />
        <div className="h-2 w-4 rounded-b-full border border-kirana-brown bg-[#cfc3a5]" />
      </div>
      <div className="h-[2px] w-12 bg-kirana-brown" />
      <div className="h-4 w-1 bg-kirana-brown" />
      <div className="h-2 w-8 rounded-sm bg-[#9C6644]" />
    </div>
    {/* stacked coins + small paper bags */}
    <div className="absolute bottom-[17%] left-[38%] flex items-end gap-1">
      <div className="flex flex-col-reverse">
        {[0, 1, 2, 3].map((i) => (
          <span key={`coin-${i}`} className="h-[3px] w-4 rounded-full border border-[#8a6a2a] bg-[#d9b44a]" />
        ))}
      </div>
      <div className="h-6 w-4 rounded-t-sm border border-[#b98e52] bg-[#d8c19a]" />
      <div className="h-5 w-4 rounded-t-sm border border-[#b98e52] bg-[#cbb489]" />
    </div>
    {/* small "UDHAAR" credit note board hung on the counter side */}
    <div className="absolute bottom-[30%] left-[7%] h-11 w-12 -rotate-3 rounded-sm border-2 border-kirana-brown bg-[#FDF6E3] p-1 shadow-[2px_2px_0_rgba(0,0,0,0.25)]">
      <div className="font-signboard text-[7px] font-bold leading-tight text-kirana-brown">
        <div className="text-center text-kirana-red">UDHAAR</div>
        <div className="text-center text-[6px]">band hai 🙏</div>
        <div className="mt-0.5 border-t border-kirana-brown/40 pt-0.5 text-[6px]">Sharma ₹20<br />Guddu ₹5</div>
      </div>
    </div>

    {/* ===== FLOOR: cartons, crates, boxes (kept to the sides, path stays clear) ===== */}
    <div className="absolute bottom-[6%] right-[4%]"><Carton w={48} h={40} rot={-3} label="PARLE" /></div>
    <div className="absolute bottom-[7%] right-[16%]"><Carton w={40} h={30} rot={2} label="LUX" /></div>
    <div className="absolute bottom-[22%] right-[6%]"><Carton w={44} h={26} rot={1} label="MAGGI" /></div>
    <div className="absolute bottom-[6%] right-[27%]"><Carton w={30} h={44} rot={-2} label="TEA" /></div>
    {/* wooden crate with bottles bottom-right */}
    <div className="absolute bottom-[6%] right-[36%] flex h-11 w-16 items-end justify-around rounded-sm border-2 border-[#7a5a3a] bg-[#9c7a52] px-1 pb-1">
      {["#2A9D8F", "#E63946", "#E9C46A", "#457B9D"].map((c, i) => (
        <span key={`bot-${i}`} className="h-8 w-2 rounded-t-full border border-black/30" style={{ background: c }} />
      ))}
    </div>
    {/* a couple of stacked matchbox-size boxes near the counter */}
    <div className="absolute bottom-[6%] left-[54%] flex flex-col items-center gap-[2px]">
      <Packet w={26} h={12} color="#D62828" stripe="#ffffff44" />
      <Packet w={30} h={14} color="#457B9D" stripe="#ffffff44" />
    </div>

    {/* open UDHAAR KHATA ledger on the counter */}
    <div className="absolute bottom-[17%] left-[20%] h-14 w-24 -rotate-2 rounded-sm border-2 border-[#7a5a3a] bg-[#FBF3DC] shadow-[2px_2px_0_rgba(0,0,0,0.25)]">
      <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-[#7a5a3a]/50" />
      <div className="px-1 pt-0.5 font-signboard text-[6px] leading-[1.35] text-[#5c3d22]">
        <div className="text-center font-bold text-kirana-red">UDHAAR KHATA</div>
        <div className="flex justify-between"><span>Ramesh</span><span>₹43</span></div>
        <div className="flex justify-between"><span>Pappu</span><span>₹27</span></div>
        <div className="flex justify-between"><span>Sharma Ji</span><span>₹112</span></div>
        <div className="flex justify-between"><span>Mohan</span><span>₹56</span></div>
        <div className="mt-[1px] text-center text-[5px] italic text-kirana-green">Aaj cash dena :)</div>
      </div>
    </div>
    {/* steel chai glass + old calculator */}
    <div className="absolute bottom-[17%] left-[46%] h-4 w-3 rounded-b-sm border border-[#8a8a8a] bg-gradient-to-b from-[#d9d9d9] to-[#a9a9a9]">
      <span className="absolute -top-1 left-1/2 h-1 w-3 -translate-x-1/2 rounded-full bg-[#b5651d]/60" />
    </div>
    <div className="absolute bottom-[16%] left-[50%] h-5 w-7 rotate-3 rounded-sm border border-[#3a2617] bg-[#2b2b2b]">
      <div className="mx-auto mt-[2px] h-1 w-5 rounded-[1px] bg-[#8fb38f]" />
      <div className="mx-auto mt-[1px] grid w-5 grid-cols-4 gap-[1px]">
        {Array.from({ length: 8 }).map((_, i) => (<span key={i} className="h-[2px] w-full bg-[#666]" />))}
      </div>
    </div>
  </div>
);
